import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EventUtils } from 'app/event/utils/event-utils';
import { LegalEntity } from 'app/event/models/legal-entity';
import { LegalEntitySearch } from 'app/event/models/legal-entity-search';
import { Party } from 'app/event/models/party';
import { Payment } from 'app/event/models/payment';
import { FlashMessagesService } from 'app/shared/flash-messages/service/flash.messages.service';
import { Observable } from 'rxjs/Observable';
import { OnyxEvent } from '../../event/models/event';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { PaymentEquation } from '../model/payment-equation';
import { PaymentRequest } from '../model/payment-request';
import { PaymentActionEnum, PaymentActions } from '../payment-action.enum';
import { PaymentRejectionReasonsComponent } from '../payment-rejection-reasons/payment-rejection-reasons.component';
import { PaymentRequestStatusEnum } from '../payment-request-status.enum';
import { CommissionService, CommissionStore } from '../service';
import { PaymentStatus } from 'app/event/models/payment-status.enum';

@Component({
  selector: 'pioneer-payment-action',
  templateUrl: './payment-action.component.html',
  styleUrls: ['./payment-action.component.css']
})
export class PaymentActionComponent implements OnInit {

  @ViewChild(PaymentRejectionReasonsComponent) rejectionReason: PaymentRejectionReasonsComponent;

  event: OnyxEvent;
  paymentEquation: PaymentEquation;

  action: string;
  actioned: string;
  oppositeAction: string;

  // page info
  title: string;
  summary: string;
  summary2: string;
  emailLabel: string;
  invoiceGenerated: string;
  pendingWorkflow: boolean;

  isCompleted = false;

  approverType: String;

  fundCurrency: String;

  isEditEmailDisabled = false;

  actionToUpdate: string;

  form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.maxLength(75), PioneerValidators.isValidEmail]]
  });

  legalEntity: LegalEntity;

  get email() { return this.form.get('email'); }

  applyOoiv = true;

  vendor: Party;

  isValidLegalEntityForm = false;

  showLegalEntity = false;

  agencyCountry = null;
  vendorCountry = null;
  attachmentId: string = null;
  status: PaymentStatus | string;

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private commissionStore: CommissionStore,
    private commissionService: CommissionService,
    private fb: FormBuilder,
    private flashMessagesService: FlashMessagesService
  ) { }

  ngOnInit() {
    this.route.queryParams
      .filter(params => params.action)
      .flatMap(params => {
        if (this.isCompleted) {
          return Observable.empty();
        }
        this.actionToUpdate = params.action;

        if (this.actionToUpdate !== PaymentActionEnum.CONFIRM.toString()) {
          this.showLegalEntity = false;
        }

        this.updateSummary(params.action);
        this.form = this.fb.group({
          email: [this.email.value, [Validators.required, Validators.maxLength(75), PioneerValidators.isValidEmail]]
        });
        return this.commissionStore.paymentRequest;
      })
      .subscribe((paymentRequest: PaymentRequest) => {
        this.event = paymentRequest.event;
        this.approverType = paymentRequest.approverType;
        this.fundCurrency = paymentRequest.fundCurrency;

        this.vendor = this.getPaymentVendor(paymentRequest.finalPayment);
        this.status = paymentRequest.finalPayment.status; // paymentRequest.finalPayment.status is a string at runtime

        let agencyId = null;
        if (this.event && this.event.eventAgency) {
          agencyId = this.event.eventAgency.code;
          this.agencyCountry = this.event.eventAgency.countryCode;
        }
        if (this.vendor) {
          this.vendorCountry = this.vendor.countryCode;
        }

        this.commissionService.isOoivEnabled(agencyId, this.agencyCountry, this.vendorCountry)
          .subscribe(ooivEnabled => {
            this.applyOoiv = ooivEnabled;

            if (ooivEnabled) {
              if (this.vendor && !this.vendor.subscriber && this.isHotelApprover()
                  && this.actionToUpdate === PaymentActionEnum.CONFIRM.toString()) {
                this.commissionService.getLegalEntityInfo(this.getLegalEntitySearch(this.vendor))
                .subscribe((legalEntity: LegalEntity) => {
                  this.legalEntity = legalEntity;

                  this.showLegalEntity = true;

                  this.updateSummary(this.actionToUpdate);
                });
              }
            }
        });

        this.updateSummary(this.actionToUpdate);
        if (!this.isHotelApprover() && paymentRequest.event && paymentRequest.event.eventAgency ) {
          this.email.setValue(this.event.eventAgency.contact.email);
          if (this.event.eventAgency.contact && this.event.eventAgency.contact.email &&  this.event.eventAgency.contact.email.length > 0) {
            this.isEditEmailDisabled = true;
            this.email.disable();
          }
        }
        if (paymentRequest.event && paymentRequest.event.eventProperty) {
          this.event.eventReferenceNumber = paymentRequest.event.eventProperty.eventReferenceNumber;
        }
        this.paymentEquation = new PaymentEquation({
          roomCommissions: paymentRequest.roomCommissions,
          adjustments: paymentRequest.adjustments,
          prepayment: paymentRequest.prepayment,
          totalPaymentRequested: paymentRequest.finalPayment ? paymentRequest.finalPayment.commissionAmount : 0.00,
          totalEstimatedTax: paymentRequest.estimatedTax
        });
      });

      this.pendingWorkflow = false;
  }

  isValidEmail() {
    return this.email.valid || this.isEditEmailDisabled;
  }

  disableForm() {
    return (this.action === PaymentActionEnum.CONFIRM.toString() && !this.isValidEmail())
      || (this.action === PaymentActionEnum.REJECT.toString() && (!this.form.valid || !this.isValidEmail()))
      || !this.validLegalEntityForm();
  }

  onSubmit() {
    let summary;
    this.invoiceGenerated = null;

    if (this.action === PaymentActionEnum.CONFIRM.toString()) {
      this.commissionService.approvePaymentRequest(this.email.value, this.legalEntity, this.attachmentId)
        .subscribe(() => {
          this.isCompleted = true;
          this.showLegalEntity = false;
          if (this.status === PaymentStatus.PENDING_WF_APPROVAL) {
            summary = this.translate.instant('EVENT.COMMISSION.CONFIRMED_SUMMARY_APPROVAL_WORKFLOW');
            this.pendingWorkflow = true;
          } else if (this.isHotelApprover()) {
            summary = this.translate.instant('EVENT.COMMISSION.CONFIRMED_SUMMARY');
            if (this.applyOoiv) {
              this.invoiceGenerated = this.translate.instant('EVENT.COMMISSION.CONFIRMED_INVOICE_CREATED');
            }
          } else {
            summary = this.translate.instant('EVENT.COMMISSION.CONFIRMED_SUMMARY_AGENCY_APPROVE');
          }
          this.setPageInfo(
            this.translate.instant('EVENT.COMMISSION.ACTIONED', { actioned: this.actioned }),
            summary);
          this.flashMessagesService.success('EVENT.COMMISSION.ACTIONED', null, { actioned: this.actioned });
        }, (err) => {
          this.isCompleted = false;
          if (err.includes('Unable to request final payment')) {
            this.flashMessagesService.error('MESSAGES.ERROR.REQUEST_INVOICE_HEALTH', err);
          } else {
            this.flashMessagesService.error('MESSAGES.ERROR.APPROVE_PAYMENT_REQUEST', `Error approving payment request: ${ err }`);
          }
          return Observable.of(err);
        });

    } else {
      this.commissionService.rejectPaymentRequest(
        this.email.value,
        this.rejectionReason.description.value,
        this.rejectionReason.details.value,
        this.attachmentId)
        .subscribe(() => {
          this.isCompleted = true;
          if (this.status === PaymentStatus.PENDING_WF_APPROVAL) {
            summary = this.translate.instant('EVENT.COMMISSION.REJECTED_SUMMARY_APPROVAL_WORKFLOW');
            this.summary2 = this.translate.instant('EVENT.COMMISSION.REJECTED_SUMMARY_APPROVAL_WORKFLOW2');
          } else if (this.isHotelApprover()) {
            summary = this.translate.instant('EVENT.COMMISSION.REJECTED_SUMMARY');
          } else {
            summary = this.translate.instant('EVENT.COMMISSION.REJECTED_SUMMARY_AGENCY_APPROVE');
          }
          this.setPageInfo(
            this.translate.instant('EVENT.COMMISSION.ACTIONED', { actioned: this.actioned }),
            summary);
          this.flashMessagesService.success('EVENT.COMMISSION.ACTIONED', null, { actioned: this.actioned });
        }, (err) => {
          this.isCompleted = false;
          this.flashMessagesService.error('MESSAGES.ERROR.REJECT_PAYMENT_REQUEST', `Error rejecting payment request: ${ err }`);
          return Observable.of(err);
        }
        );
    }

  }

  updateSummary(action: string) {
    if (PaymentActions.indexOf(action) >= 0) {
      this.action = action;

      this.translate.get('EVENT.COMMISSION.CONFIRM').subscribe(() => {
        if (action === PaymentActionEnum.CONFIRM.toString()) {
          this.setConfirmDetails();
        } else {
          this.setRejectDetails();
        }
      });
    }
  }

  setConfirmDetails() {
    this.actioned = PaymentRequestStatusEnum.CONFIRMED.toString();
    this.oppositeAction = PaymentActionEnum.REJECT.toString();
    let confirmSummaryTxt = '';
    if (!this.isHotelApprover()) {
      confirmSummaryTxt = this.translate.instant('EVENT.COMMISSION.CONFIRM_SUMMARY_AGENCY');
    } else if (this.status === PaymentStatus.PENDING_WF_APPROVAL) {
      confirmSummaryTxt = this.translate.instant('EVENT.COMMISSION.PENDING_WF_APPROVAL.SUMMARY1');
      this.summary2 = this.translate.instant('EVENT.COMMISSION.PENDING_WF_APPROVAL.SUMMARY2');
    } else {
      confirmSummaryTxt = this.translate.instant('EVENT.COMMISSION.CONFIRM_SUMMARY');
      if (this.showLegalEntityForm()) {
        confirmSummaryTxt = '';
      }
    }

    this.setPageInfo(
      this.translate.instant('EVENT.COMMISSION.ACTION', { action: PaymentActionEnum.CONFIRM.toString() }),
      confirmSummaryTxt,
      this.translate.instant('EVENT.COMMISSION.ACTION_BY', { actionBy: PaymentRequestStatusEnum.APPROVED.toString() })
    );
  }

  setRejectDetails() {
    this.actioned = PaymentRequestStatusEnum.REJECTED.toString();
    this.oppositeAction = PaymentActionEnum.CONFIRM.toString();
    let rejectSummaryText = 'EVENT.COMMISSION.REJECT_SUMMARY';
    if (!this.isHotelApprover()) {
      rejectSummaryText = 'EVENT.COMMISSION.REJECT_SUMMARY_AGENCY_APPROVE';
    }
    if (this.status === PaymentStatus.PENDING_WF_APPROVAL) {
      rejectSummaryText = 'EVENT.COMMISSION.REJECT_SUMMARY_PENDING_WORKFLOW';
    }
    this.setPageInfo(
      this.translate.instant('EVENT.COMMISSION.ACTION', { action: PaymentActionEnum.REJECT.toString() }),
      this.translate.instant(rejectSummaryText),
      this.translate.instant('EVENT.COMMISSION.ACTION_BY', { actionBy: PaymentRequestStatusEnum.REJECTED.toString() })
    );
  }

  setPageInfo(title: string, summary: string, emailLabel?: string) {
    this.title = title;
    this.summary = summary;
    this.emailLabel = emailLabel;
  }

  getGroupCode() {
    return EventUtils.getEventGroupCode(this.event, true);
  }

  getStartDate() {
    return EventUtils.getEventStartDate(this.event, true);
  }

  getEndDate() {
    return EventUtils.getEventEndDate(this.event, true);
  }

  getEventName() {
    return EventUtils.getEventName(this.event, true);
  }

  isHotelApprover() {
    return this.approverType === 'H' ? true : false;
  }

  showCurrencyNote() {
    if (!this.vendor.subscriber && this.fundCurrency) {
      return this.event.currency === this.fundCurrency ? false : true;
    } else {
      return false;
    }
  }

  isSubscriber(): boolean {
    return this.vendor.subscriber;
  }

  getPaymentVendor(payment: Payment): Party {
    let vendor: Party = null;
    if (this.event && this.event.eventProperty) {
      vendor = this.event.eventProperty;
    }

    if (payment !== null && payment.eventPartyId !== null) {
      this.event.eventVendors.forEach((item) => {
        if (item.eventPartyId === payment.eventPartyId) {
          vendor = item;
        }
      });
    }

    return vendor;
  }

  validLegalEntityForm(): boolean {
    return this.showLegalEntityForm() ? this.isValidLegalEntityForm : true;
  }

  showLegalEntityForm(): boolean {
    if (this.isHotelApprover()) {
      return this.showLegalEntity;
    } else {
      return false;
    }
  }

  setValidForm(value: boolean) {
    this.isValidLegalEntityForm = value;
  }

  setResultLegalEntity(legalEntity: LegalEntity) {
    this.legalEntity = legalEntity;
  }

  getLegalEntitySearch(vendor: Party): LegalEntitySearch {
    return new LegalEntitySearch({
      'code': vendor.code,
      'chain': vendor.chain,
      'brand': vendor.brand,
      'propertyId': vendor.propertyId
    });
  }

  // Not used now, always showing the form for non-subscriber users
  validLegalEntity(): boolean {
    if (this.legalEntity
      && this.legalEntity.vatNumberId
      && this.legalEntity.legalEntityName
      && this.legalEntity.country
      && this.legalEntity.address1
      && this.legalEntity.city) {
      return true;
    } else {
      return false;
    }
  }

  getAgencyGroupCode(): string {
    if (this.event && this.event.eventAgency && this.event.eventAgency.eventReferenceNumber) {
      return this.event.eventAgency.eventReferenceNumber;
    } else if (this.vendor && this.vendor.eventReferenceNumber) {
      return this.vendor.eventReferenceNumber;
    } else {
      return this.event.eventProperty.eventReferenceNumber;
    }
  }

  getVendorGroupCode(): string {
    if (this.vendor && this.vendor.eventReferenceNumber) {
      return this.vendor.eventReferenceNumber;
    } else if (this.event && this.event.eventAgency && this.event.eventAgency.eventReferenceNumber) {
      return this.event.eventAgency.eventReferenceNumber;
    } else {
      return this.event.eventProperty.eventReferenceNumber;
    }
  }

  setCurrentAttachment(attachments: any) {
    if (attachments && attachments.length > 0) {
      this.attachmentId = attachments[0].uniqueId;
    }
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/payment/payment-action/payment-action.component.ts